import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Table from '../../components/Table';
import api from '../../services/api';
import '../../styles/components.css';

const ROLES = [
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'dlc', label: 'DLC' },
    { value: 'hod', label: 'HOD' },
];

const EMPTY_FORM = {
    fullName: '',
    email: '',
    department: '',
    role: 'supervisor',
};

const EnrollStaff = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [activeTab, setActiveTab] = useState('manual'); // 'manual' | 'bulk'
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deptLoading, setDeptLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [bulkResult, setBulkResult] = useState(null);

    useEffect(() => {
        api.get('/admin/departments')
            .then(res => setDepartments(res.data || []))
            .catch(err => console.error('Failed to load departments', err))
            .finally(() => setDeptLoading(false));
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // ── Manual submit ──────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const res = await api.post('/admin/staff', formData);
            setSuccess(res.data.message || 'Staff enrolled successfully.');
            setFormData(EMPTY_FORM);
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Enrollment failed');
        } finally {
            setLoading(false);
        }
    };

    // ── Bulk upload ────────────────────────────────────
    const handleBulkUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        setError('');
        setSuccess('');
        setBulkResult(null);

        const fd = new FormData();
        fd.append('file', file);

        try {
            const res = await api.post('/admin/staff/bulk-upload', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setSuccess(res.data.message);
            setBulkResult(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Upload failed');
        } finally {
            setLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const downloadTemplate = () => {
        const headers = ['Full Name', 'Email', 'Department', 'Role'];
        const sample = ['Dr. Jane Doe', 'jane@tce.edu', 'Computer Science', 'supervisor'];
        const sample2 = ['Dr. Kumar', 'kumar@tce.edu', 'Electronics', 'hod'];
        const sample3 = ['Dr. Priya', 'priya@tce.edu', 'Mechanical', 'dlc'];

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet([headers, sample, sample2, sample3]);
        XLSX.utils.book_append_sheet(wb, ws, "Staff Template");

        // Generate and download file
        XLSX.writeFile(wb, 'staff_upload_template.xlsx');
    };

    const bulkSuccessColumns = [
        { header: 'Row', field: 'row' },
        { header: 'Name', field: 'fullName' },
        { header: 'Email', field: 'email' },
        { header: 'Role', field: 'role' },
    ];
    const bulkFailedColumns = [
        { header: 'Row', field: 'row' },
        { header: 'Error', field: 'error' },
    ];

    return (
        <Layout title="Enroll Staff">
            {/* ── Tab Nav ── */}   
            <div style={{ marginBottom: '1.5rem' }}>
                {[['manual', 'Manual Enrollment'], ['bulk', 'Bulk Upload (Excel)']].map(([key, label], idx, arr) => (
                    <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        style={{
                            padding: '0.75rem 1.5rem',
                            border: 'none',
                            background: activeTab === key ? '#3b82f6' : '#e5e7eb',
                            color: activeTab === key ? 'white' : '#374151',
                            cursor: 'pointer',
                            borderRadius: idx === 0 ? '0.5rem 0 0 0.5rem' : idx === arr.length - 1 ? '0 0.5rem 0.5rem 0' : '0',
                            fontWeight: '500',
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* ── Alerts ── */}
            {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            {success && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{success}</div>}

            {/* ══════════════ MANUAL ENROLLMENT ══════════════ */}
            {activeTab === 'manual' && (
                <Card title="Staff Enrollment Form">
                    <form onSubmit={handleSubmit}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>

                            {/* Role */}
                            <div className="form-group">
                                <label className="form-label">Role *</label>
                                <select name="role" className="form-select" value={formData.role} onChange={handleChange} required>
                                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </select>
                            </div>

                            {/* Full Name */}
                            <div className="form-group">
                                <label className="form-label">Full Name *</label>
                                <input
                                    type="text" name="fullName" className="form-input"
                                    value={formData.fullName} onChange={handleChange}
                                    required placeholder="Dr. John Smith"
                                />
                            </div>

                            {/* Email */}
                            <div className="form-group">
                                <label className="form-label">
                                    Email * <small style={{ color: '#6b7280', fontWeight: 'normal' }}>(Used for login)</small>
                                </label>
                                <input
                                    type="email" name="email" className="form-input"
                                    value={formData.email} onChange={handleChange}
                                    required placeholder="staff@tce.edu"
                                />
                                <small style={{ color: '#6b7280', display: 'block', marginTop: '0.25rem' }}>

                                </small>
                            </div>

                            {/* Department */}
                            <div className="form-group">
                                <label className="form-label">Department *</label>
                                {deptLoading ? (
                                    <select className="form-select" disabled><option>Loading departments…</option></select>
                                ) : (
                                    <select name="department" className="form-select" value={formData.department} onChange={handleChange} required>
                                        <option value="">Select Department</option>
                                        {departments.map(d => (
                                            <option key={d.id} value={d.name}>{d.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                            <Button type="submit" variant="primary" disabled={loading}>
                                {loading ? 'Enrolling…' : 'Enroll Staff Member'}
                            </Button>
                            <Button type="button" variant="secondary" onClick={() => navigate('/admin')}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                </Card>
            )}

            {/* ══════════════ BULK UPLOAD ══════════════ */}
            {activeTab === 'bulk' && (
                <Card title="Bulk Upload Staff Members">
                    <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ marginBottom: '0.5rem' }}>Instructions:</h4>
                        <ol style={{ paddingLeft: '1.5rem', color: '#6b7280' }}>
                            <li>Download the Excel file template below</li>
                            <li><strong>Role</strong> must be one of: <code>supervisor</code>, <code>dlc</code>, <code>hod</code></li>
                            <li><strong>Department</strong> must exactly match a department in the system</li>
                            <li>If an email already exists in authentication it will be replaced automatically</li>
                            <li>Save as Excel and upload below</li>
                        </ol>
                    </div>

                    {/* Columns info */}
                    <div style={{ background: '#f3f4f6', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
                        <h4 style={{ marginBottom: '0.5rem' }}>Required Columns:</h4>
                        <code style={{ fontSize: '0.875rem', color: '#374151' }}>Full Name, Email, Department, Role</code>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
                        <Button variant="secondary" onClick={downloadTemplate}>
                            📥 Download Template (XLSX)
                        </Button>
                        <div>
                            <input
                                type="file" ref={fileInputRef}
                                onChange={handleBulkUpload}
                                accept=".xlsx"
                                style={{ display: 'none' }}
                                id="staff-bulk-input"
                            />
                            <Button variant="primary" onClick={() => fileInputRef.current?.click()} disabled={loading}>
                                {loading ? 'Uploading…' : '📤 Upload File'}
                            </Button>
                        </div>
                    </div>

                    {/* Results */}
                    {bulkResult && (
                        <div style={{ marginTop: '2rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                                {[
                                    { label: 'Total Processed', value: bulkResult.totalProcessed, bg: '#dbeafe', color: '#1e40af' },
                                    { label: 'Successful', value: bulkResult.successCount, bg: '#dcfce7', color: '#166534' },
                                    { label: 'Failed', value: bulkResult.failedCount, bg: '#fee2e2', color: '#991b1b' },
                                ].map(s => (
                                    <div key={s.label} style={{ background: s.bg, padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: s.color }}>{s.value}</div>
                                        <div style={{ color: s.color }}>{s.label}</div>
                                    </div>
                                ))}
                            </div>

                            {bulkResult.success?.length > 0 && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <h4 style={{ marginBottom: '0.5rem', color: '#166534' }}>✓ Successfully Enrolled:</h4>
                                    <Table columns={bulkSuccessColumns} data={bulkResult.success} emptyMessage="" />
                                </div>
                            )}
                            {bulkResult.failed?.length > 0 && (
                                <div>
                                    <h4 style={{ marginBottom: '0.5rem', color: '#991b1b' }}>✗ Failed:</h4>
                                    <Table columns={bulkFailedColumns} data={bulkResult.failed} emptyMessage="" />
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ marginTop: '2rem' }}>
                        <Button variant="secondary" onClick={() => navigate('/admin')}>Back to Dashboard</Button>
                    </div>
                </Card>
            )}
        </Layout>
    );
};

export default EnrollStaff;
